import C from "@/modules/user/Profile.module.css";

import type { Action } from "@/components/Action.tsx";
import { Badge } from "@/components/Badge.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { WTooltip } from "@/components/WTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { useConfig } from "@/config.ts";
import { mfaDeactivateWorks } from "@/featureFlags.ts";
import { AcceptInvitationModal } from "@/modules/access/AcceptInvitationModal.tsx";
import {
    accessDelegationListQuery,
    type AccessDelegationWithType,
} from "@/modules/access/delegation/accessDelegationApi.ts";
import { accessImpersonationListQuery } from "@/modules/access/impersonation/accessImpersonationApi.ts";
import { delegationLink } from "@/modules/access/links.ts";
import { impersonationLabel } from "@/modules/access/scopeLabel.tsx";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import { useStandardMutation, useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { rawBillingAccountQueryAtom } from "@/modules/billing/billingAccountQuery.ts";
import { AddSshKeyModal } from "@/modules/compute/AddSshKeyModal.tsx";
import { SshKeyDeleteModal } from "@/modules/compute/SshKeyDeleteModal.tsx";
import { UpdateSshKeyModal } from "@/modules/compute/UpdateSshKeyModal.tsx";
import { sshKeysQuery } from "@/modules/compute/sshKeysQuery.ts";
import { authProviderAtom } from "@/modules/login/authProvider.ts";
import { raiseBackgroundErrorToast, raiseToast } from "@/modules/notifications/toast.tsx";
import { ActAsModal } from "@/modules/user/ActAsModal.tsx";
import { LogoutButton } from "@/modules/user/LogoutButton.tsx";
import { UserAvatar } from "@/modules/user/UserAvatar.tsx";
import {
    activateMfaMutation,
    changeProfileDataMutation,
    deactivateMfaMutation,
    userDataAtom,
    userMfaQuery,
} from "@/modules/user/userApi.ts";
import { openNewTab, openNewTabOrRedirect } from "@/utils/openNewTab.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useTitle } from "@/utils/react/useTitle.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import type { User } from "@warrenio/api-spec/spec.oats.gen";
import { useAtomValue } from "jotai/react";
import { sortBy } from "remeda";

export function ProfileView() {
    //#region Hooks
    const { t } = useLingui();
    useTitle(t`Profile`);

    const data = useSuspenseQueryAtom(userDataAtom);
    const changeProfileData = useStandardMutation(changeProfileDataMutation);
    const { mfaAvailable } = useConfig();
    const provider = useAtomValue(authProviderAtom);
    //#endregion

    const { name, profile_data } = data;
    const { email } = profile_data;

    async function changeProfileDataPartial(newFields: Partial<User["profile_data"]>) {
        await changeProfileData.mutateAsync({ body: newFields });
    }

    async function changeProfilePassword(email: string) {
        try {
            const message = await provider.changePassword({ email });
            raiseToast({ message, type: "success", icon: "jp-user-icon" });
        } catch (e) {
            raiseBackgroundErrorToast(e, <Trans>Changing the password failed</Trans>);
        }
    }

    return (
        <div className={C.ProfileHolder}>
            <div className="p-8 pb-4 flex justify-center">
                <UserAvatar user={data} />
            </div>

            <ContentPane>
                <div className="lightBlock VStack gap-4">
                    <div className="flex justify-between flex-wrap">
                        <h1 className="font-size-heading">{name}</h1>

                        <LogoutButton size="bar" />
                    </div>

                    <DetailsHolder>
                        <DetailsTable>
                            <DetailsTableName
                                title={t`First name`}
                                placeholder={t`Add first name`}
                                value={profile_data.first_name ?? ""}
                                isRequired
                                onChange={(value) => changeProfileDataPartial({ first_name: value })}
                            />

                            <DetailsTableName
                                title={t`Last name`}
                                placeholder={t`Add last name`}
                                value={profile_data.last_name ?? ""}
                                isRequired
                                onChange={(value) => changeProfileDataPartial({ last_name: value })}
                            />

                            <DetailsTableRow title={t`Email`}>{profile_data.email}</DetailsTableRow>

                            {email != null && (
                                <DetailsTableRow title={t`Password`}>
                                    <WButton
                                        icon="jp-icon-refresh"
                                        color="primary"
                                        variant="ghost"
                                        size="xs"
                                        action={async () => await changeProfilePassword(email)}
                                    >
                                        <Trans>Reset password</Trans>
                                    </WButton>
                                </DetailsTableRow>
                            )}

                            <DetailsTableRow title={t`Created at`}>
                                <LongDate date={apiDatetimeToDate(profile_data.created_at)} />
                            </DetailsTableRow>
                        </DetailsTable>
                    </DetailsHolder>
                </div>
            </ContentPane>

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans>Users I can access</Trans>} />
                <ContentLoadingSuspense>
                    <ImpersonationsTable />
                </ContentLoadingSuspense>
            </ContentPane>

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans>Users who can access my account</Trans>} />
                <ContentLoadingSuspense>
                    <DelegationsTable />
                </ContentLoadingSuspense>
            </ContentPane>

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans>SSH keys</Trans>} />
                <ContentLoadingSuspense>
                    <SshKeysTable />
                </ContentLoadingSuspense>
            </ContentPane>

            {mfaAvailable && (
                <>
                    <Separator />

                    <ContentPane className="VStack gap-4">
                        <SectionHeader title={<Trans>Multi-factor authentication (MFA)</Trans>} />
                        <ContentLoadingSuspense>
                            <MfaContent />
                        </ContentLoadingSuspense>
                    </ContentPane>
                </>
            )}
        </div>
    );
}

function MfaContent() {
    const { data } = useStandardSuspenseQuery(userMfaQuery);
    const activateMutation = useStandardMutation(activateMfaMutation);
    const deactivateMutation = useStandardMutation(deactivateMfaMutation);

    async function activateMfa() {
        // Reserve the tab during the user gesture so popup blockers do not force the
        // current tab into the one-time Auth0 enrollment link after the async request.
        const newTab = openNewTab();
        try {
            const { ticket_url } = await activateMutation.mutateAsync();
            openNewTabOrRedirect(ticket_url, newTab);
        } catch (error) {
            newTab?.close();
            throw error;
        }
    }

    async function deactivateMfa() {
        await deactivateMutation.mutateAsync();
    }

    const { success } = data;

    return (
        <div className="VStack gap-4">
            <p className="color-muted">
                <Trans>
                    You can activate multi-factor authentication for improved security. This works only for the
                    username-password login method. For social login methods (Google etc.), it is usually possible to
                    activate MFA on the third party side.
                </Trans>
            </p>

            {success ? (
                <div className="VStack gap-2">
                    <p>
                        <Trans>MFA is active for your user.</Trans>
                    </p>

                    {mfaDeactivateWorks && (
                        <WButton color="primary" size="bar" variant="border" action={async () => await deactivateMfa()}>
                            <Trans>Disable MFA</Trans>
                        </WButton>
                    )}
                </div>
            ) : (
                <WButton color="primary" size="bar" variant="border" action={async () => await activateMfa()}>
                    <Trans>Enable MFA</Trans>
                </WButton>
            )}
        </div>
    );
}

function ImpersonationsTable() {
    const { data } = useStandardSuspenseQuery(accessImpersonationListQuery);
    const impersonations = sortBy([...data.values()], [(a) => a.created_at, "desc"]);

    return (
        <WTable>
            <thead>
                <tr>
                    <th>
                        <Trans>Name</Trans>
                    </th>
                    <th>
                        <Trans>Access scope</Trans>
                    </th>
                    <th>
                        <Trans>Date</Trans>
                    </th>
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {impersonations.map((item) => {
                    return (
                        <tr key={item.uuid}>
                            <td>{item.access_owner}</td>
                            <td>{impersonationLabel(item.billing_account_name)}</td>
                            <td>
                                <LongDate date={item.created_at} />
                            </td>
                            <td>
                                {item.is_accepted ? (
                                    <ActAsModal item={item} />
                                ) : (
                                    <AcceptInvitationModal uuid={item.uuid} name={item.access_owner} />
                                )}
                            </td>
                        </tr>
                    );
                })}
            </WTableBody>
        </WTable>
    );
}

const ViewImpersonationLink = ({
    delegation,
    viewLink,
}: {
    delegation: AccessDelegationWithType;
    viewLink: Action;
}) => (
    <WTooltip text={<Trans>View</Trans>}>
        <WButton color="primary" size="xs" variant="ghost" action={viewLink}>
            {delegation.name}
        </WButton>
    </WTooltip>
);

function DelegationsTable() {
    const { data } = useStandardSuspenseQuery(accessDelegationListQuery);
    const accounts = useSuspenseQueryAtom(rawBillingAccountQueryAtom);
    const delegations = sortBy([...data.values()], [(a) => a.created_at, "desc"]);

    return (
        <WTable
            afterTable={
                <WButton
                    color="primary"
                    size="bar"
                    variant="border"
                    icon="jp-icon-add"
                    action={ResourceCreateLinks.access_delegation}
                >
                    <Trans>Delegate new access</Trans>
                </WButton>
            }
        >
            <thead>
                <tr>
                    <th>
                        <Trans>Access group</Trans>
                    </th>
                    <th>
                        <Trans>Scope</Trans>
                    </th>
                    <th>
                        <Trans>Status</Trans>
                    </th>
                    <th>
                        <Trans comment="Created at timestamp">Created</Trans>
                    </th>
                </tr>
            </thead>
            <WTableBody>
                {delegations.map((item) => {
                    const viewLink = delegationLink(item);

                    let acceptedSum = 0;
                    let pendingSum = 0;

                    for (const child of item.impersonations) {
                        if (child.is_accepted) {
                            acceptedSum++;
                        } else {
                            pendingSum++;
                        }
                    }

                    const billingAccountName = item.billing_account_id
                        ? (accounts.get(item.billing_account_id)?.title ?? null)
                        : undefined;

                    return (
                        <tr key={item.id}>
                            <td>
                                <ViewImpersonationLink delegation={item} viewLink={viewLink} />
                            </td>
                            <td>{impersonationLabel(billingAccountName)}</td>
                            <td>
                                {!!acceptedSum && (
                                    <Badge color="success">
                                        {acceptedSum} <Trans>Accepted</Trans>
                                    </Badge>
                                )}
                                {!!pendingSum && (
                                    <Badge color="warning">
                                        {pendingSum} <Trans>Pending</Trans>
                                    </Badge>
                                )}
                                {!acceptedSum && !pendingSum && "-"}
                            </td>
                            <td>
                                <LongDate date={item.created_at} />
                            </td>
                        </tr>
                    );
                })}
            </WTableBody>
        </WTable>
    );
}

function SshKeysTable() {
    const { data: sshkeys } = useStandardSuspenseQuery(sshKeysQuery);

    const items = sortBy([...sshkeys.values()], [(a) => a.created_at, "desc"]);

    return (
        <WTable afterTable={<AddSshKeyModal />}>
            <thead>
                <tr>
                    <th>
                        <Trans>Name</Trans>
                    </th>
                    <th>
                        <Trans>Public key</Trans>
                    </th>
                    <th>
                        <Trans>Created</Trans>
                    </th>
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {items.map((item, index) => (
                    <tr key={index}>
                        <td>{item.name}</td>
                        <td>
                            <ClipBoardTooltip text={item.public_key}>
                                <span className="max-w-16rem text-ellipsis overflow-x-hidden">{item.public_key}</span>
                            </ClipBoardTooltip>
                        </td>
                        <td>
                            <LongDate date={item.created_at} />
                        </td>
                        <td className="text-right">
                            <div className="flex gap-0.5em justify-end">
                                <UpdateSshKeyModal item={item} />
                                <SshKeyDeleteModal item={item} />
                            </div>
                        </td>
                    </tr>
                ))}
            </WTableBody>
        </WTable>
    );
}
